/**
 * identityNormalizers.js — Rudrik
 * ================================
 * Per-source mappers that convert each module's raw API response into the
 * unified `person` shape consumed by <IdentityResultCard />.
 *
 * Unified shape (all keys optional; the card shows "Not available" for any
 * missing field — never silently hide a row):
 *
 *   {
 *     photo, name, aliases, risk_level,
 *     dob, place_of_birth, gender, nationality, parent_or_spouse,
 *     pan, aadhaar_masked, passport, voter_id, din_cin,
 *     address: { line, city, state, pincode, country },
 *     source:  { label, fetched_at, confidence, kind },  // kind: official|llm|news
 *     risk_flags: [{ severity, label, detail }],
 *   }
 */

const nowIso = () => new Date().toISOString();

// ---------- shared mappers ----------

function severityFromRisk(level) {
  const m = (level || "").toString().toUpperCase();
  return ["LOW", "MEDIUM", "HIGH", "CRITICAL", "WARNING"].includes(m) ? m : "UNKNOWN";
}

function mapRiskFlags(flags) {
  if (!Array.isArray(flags)) return [];
  return flags.map((f) => ({
    severity: (f.severity || f.sev || "medium").toString().toLowerCase(),
    label: f.label || f.display_text || f.title || f.type || f.flag || "Flag",
    detail: f.detail || f.reason || f.description || null,
  }));
}

// ---------- PAN VERIFY ----------
// /api/pan-verify/lookup → { result: {...} }
export function fromPanVerify(result, query) {
  if (!result) return null;
  const vr = result.verification_result || {};
  const ra = result.risk_assessment || {};
  return {
    photo: null,                                  // PAN API has no photo
    name: vr.name_on_pan || result.name_on_pan || null,
    aliases: [],
    risk_level: ra.overall_risk || result.risk_level,
    dob: vr.date_of_birth || null,
    place_of_birth: null,
    gender: null,
    nationality: vr.pan_type ? "Indian" : null,    // PAN is Indian-only
    parent_or_spouse: null,
    pan: result.pan_number || query || null,
    aadhaar_masked: result.aadhaar_linkage?.linked ? "Linked (masked)" : null,
    passport: null,
    voter_id: null,
    din_cin: null,
    address: {},
    source: {
      label: result.screening_meta?.powered_by || "Income Tax Dept · PAN DB",
      fetched_at: result.screening_meta?.screened_at || nowIso(),
      confidence: result.ai_mode === "live-gridlines" ? "live" : "deterministic",
      kind: "official",
    },
    risk_flags: mapRiskFlags(ra.risk_flags),
  };
}

// ---------- AADHAAR OKYC ----------
// /api/aadhaar/verify-otp → { result: {...} }
export function fromAadhaarOkyc(result) {
  if (!result) return null;
  return {
    photo: result.photo_base64 || null,
    name: result.name || null,
    aliases: [],
    risk_level: result.kyc_verified ? "LOW" : "MEDIUM",
    dob: result.date_of_birth || null,
    place_of_birth: result.address?.state || null,
    gender: result.gender || null,
    nationality: "Indian",
    parent_or_spouse: result.care_of || null,
    pan: null,
    aadhaar_masked: result.aadhaar_number_masked || null,
    passport: null,
    voter_id: null,
    din_cin: null,
    address: {
      line: [result.address?.house, result.address?.street].filter(Boolean).join(", ") || null,
      city: result.address?.city || result.address?.district || null,
      state: result.address?.state || null,
      pincode: result.address?.pincode || null,
      country: result.address?.country || "India",
    },
    source: {
      label: `UIDAI Aadhaar OKYC · ${result.provider || "Gridlines"}`,
      fetched_at: result.verified_at || nowIso(),
      confidence: result.kyc_verified ? "verified" : "unverified",
      kind: "official",
    },
    risk_flags: result.kyc_verified ? [] : [{
      severity: "medium",
      label: "KYC not verified",
      detail: result.error_message || "OTP verification did not complete",
    }],
  };
}

// ---------- DIGILOCKER e-AADHAAR (via Surepass) ----------
// /api/v1/surepass/digilocker/download-aadhaar → { care_of, full_name, gender,
//   dob, masked_aadhaar, address, profile_image, _source, _fetched_at }
const _dlEmpty = (v) => v == null || v === "" || v === "N/A" || v === "Not available on record";
const _dlVal = (v) => (_dlEmpty(v) ? null : v);

export function fromDigilockerAadhaar(result) {
  if (!result) return null;
  const addr = result.address || {};
  const line = [addr.house, addr.street, addr.landmark, addr.loc, addr.po]
                 .map((x) => (typeof x === "string" ? x.trim() : x))
                 .filter(Boolean).join(", ") || null;
  return {
    photo: _dlVal(result.profile_image),
    name: _dlVal(result.full_name),
    aliases: [],
    risk_level: "LOW",
    dob: _dlVal(result.dob),
    place_of_birth: _dlVal(addr.state) || _dlVal(addr.dist) || null,
    gender: _dlVal(result.gender),
    nationality: "Indian",
    parent_or_spouse: _dlVal(result.care_of),
    pan: null,
    aadhaar_masked: _dlVal(result.masked_aadhaar),
    passport: null,
    voter_id: null,
    din_cin: null,
    address: {
      line,
      city: _dlVal(addr.vtc) || _dlVal(addr.city) || _dlVal(addr.dist) || null,
      state: _dlVal(addr.state),
      pincode: _dlVal(addr.pincode) || _dlVal(addr.zip),
      country: _dlVal(addr.country) || "India",
    },
    source: {
      label: result?._source?.provider || "DigiLocker · UIDAI e-Aadhaar",
      fetched_at: result?._fetched_at || nowIso(),
      confidence: "verified",
      kind: "official",
    },
    risk_flags: [],
  };
}

// ---------- FACE MATCH ----------
// /api/face-match → { result: {...}, selfie_base64? }
export function fromFaceMatch(result, selfieBase64 = null) {
  if (!result) return null;
  const overall = result.overall_result; // PASS | FAIL | MANUAL_REVIEW
  const riskMap = { PASS: "LOW", FAIL: "CRITICAL", MANUAL_REVIEW: "MEDIUM" };
  return {
    photo: selfieBase64,
    name: null,                                  // face match doesn't return a name
    aliases: [],
    risk_level: riskMap[overall] || result.risk_level || "UNKNOWN",
    dob: null, place_of_birth: null, gender: null,
    nationality: null, parent_or_spouse: null,
    pan: null, aadhaar_masked: null, passport: null, voter_id: null, din_cin: null,
    address: {},
    source: {
      label: result.source || `Face Match · ${result.ai_mode || "AWS Rekognition"}`,
      fetched_at: result.checked_at || nowIso(),
      confidence: result.face_match?.confidence != null
        ? `${result.face_match.confidence.toFixed(1)}% similarity`
        : overall,
      kind: "official",
    },
    risk_flags: mapRiskFlags(result.risk_flags),
  };
}

// ---------- OPEN-SANCTIONS / GLOBAL SANCTIONS ----------
// /api/opensanctions/lookup → { result: {...}, risk: {...} }
export function fromSanctions(result, risk, query) {
  if (!result) return null;
  const top = (result.matches || [])[0] || {};
  const aliases = [
    ...(top.aliases || []),
    ...(result.aliases || []),
  ].filter((a) => a && a !== (top.name || ""));
  return {
    photo: null,
    name: top.name || result.query_name || query || null,
    aliases: Array.from(new Set(aliases)),
    risk_level: result.overall_risk || risk?.severity_level?.toUpperCase() || "UNKNOWN",
    dob: top.birth_date || top.dob || null,
    place_of_birth: top.place_of_birth || top.pob || null,
    gender: top.gender || null,
    nationality: top.nationality || top.country || null,
    parent_or_spouse: null,
    pan: null, aadhaar_masked: null,
    passport: top.passport || null,
    voter_id: null,
    din_cin: top.din || null,
    address: {
      line: top.address || null,
      country: top.country || null,
    },
    source: {
      label: result.source || `OpenSanctions · ${(top.lists_matched || top.datasets || []).slice(0, 2).join(", ") || "200+ datasets"}`,
      fetched_at: result.screened_at || nowIso(),
      confidence: top.score != null ? `${top.score}% match` : (result.total_matches ? `${result.total_matches} match(es)` : "no matches"),
      kind: "official",
    },
    risk_flags: mapRiskFlags(result.risk_flags || risk?.flags),
  };
}

// ---------- ENFORCEMENT (ED / CBI / SFIO) ----------
// /api/enforcement/lookup → { result: {...}, risk: {...} }
export function fromEnforcement(result, risk) {
  if (!result) return null;
  const ed = result.ed_data || {};
  const cbi = result.cbi_data || {};
  const sfio = result.sfio_data || {};
  const allPersons = [
    ...(ed.persons || []),
    ...(cbi.persons || []),
    ...(sfio.directors_accused || []),
  ];
  const primary = result.query_name || allPersons[0];
  return {
    photo: null,
    name: primary,
    aliases: result.aliases || [],
    risk_level: (risk?.severity_level || "low").toUpperCase(),
    dob: result.dob || null,
    place_of_birth: result.place_of_birth || null,
    gender: null,
    nationality: "Indian",
    parent_or_spouse: null,
    pan: null, aadhaar_masked: null, passport: null, voter_id: null,
    din_cin: result.din || null,
    address: {},
    source: {
      label: "ED · CBI · SFIO press releases & news",
      fetched_at: result.checked_at || nowIso(),
      confidence: result.ai_mode || "claude-extracted",
      kind: "news",
    },
    risk_flags: mapRiskFlags(risk?.flags),
  };
}

// ---------- E-COURTS (NJDG) ----------
// /api/ecourts/search → various
export function fromCourtRecords(result, query) {
  if (!result) return null;
  const cases = result.cases || result.matches || [];
  const first = cases[0] || {};
  return {
    photo: null,
    name: result.name || first.party_name || first.petitioner || query || null,
    aliases: first.aliases || [],
    risk_level: result.risk_level || (cases.length > 0 ? "MEDIUM" : "LOW"),
    dob: result.dob || first.dob || null,
    place_of_birth: null,
    gender: result.gender || null,
    nationality: "Indian",
    parent_or_spouse: result.father_name || first.father_name || null,
    pan: null, aadhaar_masked: null, passport: null, voter_id: null, din_cin: null,
    address: {
      state: first.state || null,
      city: first.district || null,
    },
    source: {
      label: "eCourts · NJDG · National Judicial Data Grid",
      fetched_at: result.fetched_at || nowIso(),
      confidence: `${cases.length} case(s) found`,
      kind: "official",
    },
    risk_flags: mapRiskFlags(result.risk_flags),
  };
}

// ---------- WATCHLIST (internal DB) ----------
// /api/watchlist/search → { matches: [...] }
export function fromWatchlist(match, query) {
  if (!match) {
    return {
      name: query || null,
      aliases: [],
      risk_level: "LOW",
      address: {},
      source: { label: "Rudrik Watchlist DB", fetched_at: nowIso(), confidence: "no matches", kind: "official" },
      risk_flags: [],
    };
  }
  return {
    photo: match.photo || null,
    name: match.name || match.full_name || query,
    aliases: match.aliases || match.aka || [],
    risk_level: (match.severity || match.risk_level || "high").toUpperCase(),
    dob: match.dob || match.date_of_birth || null,
    place_of_birth: match.place_of_birth || null,
    gender: match.gender || null,
    nationality: match.nationality || match.country || null,
    parent_or_spouse: match.father_name || null,
    pan: match.pan || null,
    aadhaar_masked: match.aadhaar_last4 ? `XXXX XXXX ${match.aadhaar_last4}` : null,
    passport: match.passport || null,
    voter_id: match.voter_id || null,
    din_cin: match.din || match.cin || null,
    address: {
      line: match.address || null,
      country: match.country || null,
    },
    source: {
      label: `Watchlist · ${match.list_source || match.source || "Rudrik DB"}`,
      fetched_at: match.fetched_at || nowIso(),
      confidence: match.match_score != null ? `${match.match_score}% match` : "exact",
      kind: "official",
    },
    risk_flags: mapRiskFlags(match.risk_flags || match.flags),
  };
}

// ---------- 360° ENTITY INTELLIGENCE ----------
export function fromEntityIntel(result, query) {
  if (!result) return null;
  const profile = result.profile || result.entity || {};
  return {
    photo: profile.photo || null,
    name: profile.name || result.query || query || null,
    aliases: profile.aliases || result.aliases || [],
    risk_level: result.overall_risk || profile.risk_level || "UNKNOWN",
    dob: profile.dob || null,
    place_of_birth: profile.place_of_birth || null,
    gender: profile.gender || null,
    nationality: profile.nationality || null,
    parent_or_spouse: profile.father_name || null,
    pan: profile.pan || null,
    aadhaar_masked: profile.aadhaar_last4 ? `XXXX XXXX ${profile.aadhaar_last4}` : null,
    passport: profile.passport || null,
    voter_id: profile.voter_id || null,
    din_cin: profile.din || profile.cin || null,
    address: profile.address || {},
    source: {
      label: "Rudrik 360° Entity Intelligence",
      fetched_at: result.fetched_at || nowIso(),
      confidence: result.sources_checked != null ? `${result.sources_checked} sources` : "aggregated",
      kind: "official",
    },
    risk_flags: mapRiskFlags(result.risk_flags),
  };
}

// ---------- SCREENING HUB (multi-source aggregate) ----------
export function fromScreeningHub(result, query) {
  if (!result) return null;
  return {
    photo: result.photo || null,
    name: result.name || result.query || query || null,
    aliases: result.aliases || [],
    risk_level: result.overall_risk || result.risk_level || "UNKNOWN",
    dob: result.dob || null,
    place_of_birth: result.place_of_birth || null,
    gender: result.gender || null,
    nationality: result.nationality || null,
    parent_or_spouse: result.father_name || null,
    pan: result.pan || null,
    aadhaar_masked: result.aadhaar_masked || null,
    passport: result.passport || null,
    voter_id: null,
    din_cin: result.din || result.cin || null,
    address: result.address || {},
    source: {
      label: "Rudrik Screening Hub · multi-source",
      fetched_at: result.screened_at || nowIso(),
      confidence: result.sources_checked != null ? `${result.sources_checked} sources` : null,
      kind: "official",
    },
    risk_flags: mapRiskFlags(result.risk_flags),
  };
}

// ---------- KYC JOURNEY (final report) ----------
export function fromKycJourney(journey) {
  if (!journey) return null;
  const c = journey.customer || {};
  const cd = c.customer_data || {};
  return {
    photo: cd.photo_base64 || journey.photo || null,
    name: cd.full_name || cd.name || c.name || null,
    aliases: cd.aliases || [],
    risk_level: (c.risk_level || journey.overall_risk || "low").toUpperCase(),
    dob: cd.dob || cd.date_of_birth || null,
    place_of_birth: cd.place_of_birth || null,
    gender: cd.gender || null,
    nationality: cd.nationality || (cd.pan ? "Indian" : null),
    parent_or_spouse: cd.father_name || cd.spouse_name || null,
    pan: cd.pan || null,
    aadhaar_masked: cd.aadhaar_last4 ? `XXXX XXXX ${cd.aadhaar_last4}` : null,
    passport: cd.passport || null,
    voter_id: cd.voter_id || null,
    din_cin: cd.din || cd.cin || null,
    address: {
      line: cd.address || cd.address_line || null,
      city: cd.city || null,
      state: cd.state || null,
      pincode: cd.pincode || null,
      country: cd.country || "India",
    },
    source: {
      label: "Rudrik KYC + AML Journey · final report",
      fetched_at: journey.completed_at || journey.updated_at || nowIso(),
      confidence: c.kyc_status || journey.status || "in progress",
      kind: "official",
    },
    risk_flags: mapRiskFlags(journey.risk_flags || c.risk_flags),
  };
}
